<template>
  <div
    v-if="(allLabelNames && allLabelNames.length > 0) || showName || showTags"
    class="details"
    :title="imgName ? imgName : 'Unknown'"
    @click.stop
  >
    <div v-if="showName" data-type="labels" class="details__row">
      <div class="details__name">
        <p class="details__name-content">{{ imgName ? imgName : "Unknown" }}</p>
        <p v-if="imgName && uuid" class="">{{ uuid }}</p>
      </div>
    </div>
    <div v-if="allLabelNames && allLabelNames.length > 0" data-type="labels" class="details__row mt-2">
      <div class="details__description">Labels&nbsp;</div>
      <div class="details__tags">
        <template v-for="label in allLabelNames" :key="label">
          <span class="id-tag" :style="tagColor(label.index)">{{ label.name }}</span>
        </template>
      </div>
    </div>
    <div v-if="showTags" data-type="tags" class="details__row mt-2">
      <div class="details__description">Tags&nbsp;</div>
      <div v-if="allTagNames && allTagNames.length > 0" class="details__tags">
        <v-chip-group column>
          <v-chip
            v-for="tag in allTagNames.sort()"
            :key="tag"
            variant="outlined"
            density="compact"
            closable
            close-icon="mdi-close"
            :ripple="false"
            :link="false"
            @click:close.prevent="removeTagsForImages(tag)"
          >
            {{ tag }}
          </v-chip>
        </v-chip-group>
        <!-- <template v-for="tag in allTagNames" :key="tag">
          <span class="id-tag">{{ tag }}</span>
        </template> -->
      </div>
    </div>
  </div>
</template>

<script>
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import { toRefs } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export default {
  name: 'GalleryGridItemLabelTags',
  props: {
    tags: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    annotationList: {
      type: [Array, Object],
      default: () => [],
    },
    imageId: {
      type: Number,
      default: null,
    },
    imgName: {
      type: String,
      default: "",
    },
    showName: {
      type: Boolean,
      default: false,
    },
    showTags: {
      type: Boolean,
      default: false,
    },
    uuid: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const { labels } = toRefs(props);
    const { map: labelColorMap } = useAnnotationColorMap({ items: labels, key: 'index' });
    return {
      labelColorMap,
    };
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(),
    };
  },
  computed: {
    currentDataset() {
      return this.$store.state.datasets.currentDataset;
    },
    allLabelIndexes() {
      let annotationsArray;
      if (Array.isArray(this.annotationList)) {
        annotationsArray = this.annotationList;
      } else if (typeof this.annotationList === 'object') {
        // Convert object to array if it's not already
        annotationsArray = [this.annotationList];
      } else {
        annotationsArray = [];
      }

      if (annotationsArray) {
        return [...new Set(annotationsArray.map((anno) => anno.label_index))].sort((a, b) => a - b);
      }
      return [];
    },
    allLabelNames() {
      if (this.allLabelIndexes.length > 0) {
        return this.allLabelIndexes.map((index) => {
          const lbl = this.labels.find((label) => label.index === index);
          if (lbl) {
            return lbl;
          } else {
            // use the index as name
            return {
              index,
              name: index.toString(),
            };
          }
        });
      }
      return this.allLabelIndexes;
    },
    allTagNames() {
      if (this.tags && this.tags.length > 0) {
        return this.tags.map((e) => {
          if (typeof e === "string") {
            return e;
          } else if (typeof e === "object") {
            return e.name;
          }
          return e;
        });
      }
      return [];
    },
  },
  methods: {
    tagColor(index) {
      const label = this.labels.find((e) => e.index === index);
      let color;
      if (label && label.color) {
        color = this.parseColor(label.color);
      } else {
        color = this.labelColorMap[index];
      }
      return {
        borderColor: color,
        background: `hsla(${color},70%,50%,0.75)`,
      };
    },
    getHue(color) {
      const r = parseInt(color.substring(0, 2), 16) / 255;
      const g = parseInt(color.substring(2, 4), 16) / 255;
      const b = parseInt(color.substring(4, 6), 16) / 255;
      let hue;
      if ((r >= g) && (g >= b)) {
        hue = (60 * (g - b)) / (r - b);
      } else if ((g > r) && (r >= b)) {
        hue = 60 * (2 - (r - b) / (g - b));
      }
      return hue;
    },
    async removeTagsForImages(tag_name) {
      await this.dataConnect.removeTagForImages({
        dataset_id: this.currentDataset.id,
        image_ids: [this.imageId],
        tag_names: [tag_name],
      })
        .then((resp) => {
          if (resp.error) {
            throw resp.error;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    uint32ToHex(uint32Value) {
      if (typeof uint32Value !== 'number' || uint32Value < 0 || uint32Value > 0xFFFFFFFF || !Number.isInteger(uint32Value)) {
        throw new TypeError('Input must be a valid 32-bit unsigned integer');
      }

      // Convert the number to a hexadecimal string
      const hexString = uint32Value.toString(16);

      // Pad the string with leading zeros to ensure it has 8 characters
      return `#${hexString.padStart(8, '0').toUpperCase()}`;
    },
    parseColor(color) {
      if (typeof color === 'string') {
        let colorCode = color.slice(1);
        if (colorCode.length === 6) {
          colorCode += 'FF';
        }
        if (colorCode.length === 8) {
          colorCode = colorCode.slice(0, -2);
          colorCode += 'FF';
        }
        return colorCode;
      } else if (typeof color === 'number') {
        return this.uint32ToHex(color);
      } else {
        return null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.details {
  flex: 1 1 auto;
  height: fit-content;
  padding: 10px;
  font-size: 14px;
  text-align: left;
  border-top: 1px solid rgba(0,0,0,0.1);
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
  cursor: default;
  user-select: initial;
  @include themify() {
    background: themed('image-details-background-fill');
  }

  &__row {
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  &__row + .tags__row {
    margin-top: 3px;
  }

  &__description {
    font-size: 0.75rem;
    font-weight: bold;
    padding-right: 12px;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  &__tags {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    @include themify() {
      color: themed('color-black');
    }
  }

  &__name {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    line-height: 1.2rem;
    border-radius: 4px;
  }
  &__name-content {
    display: -webkit-box;
    width: 100%;
    font-size: 12px;
    font-weight: 700;
    margin: 0;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-break: anywhere;
  }
}

.id-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  padding: 4px 6px;
  line-height: 1;
  font-size: 0.625rem;
  font-weight: 700;
  border: 2px solid;
  box-shadow: 0px 0px 1px rgb(0 0 0 / 25%), 0px 1px 4px rgb(0 0 0 / 25%);
  @include themify() {
    color: themed('color-black');
  }
}

:deep(.v-chip-group) {
  padding: 0;
}

:deep(.v-chip__close) {
  display: none;
}

:deep(.v-chip) {
  margin: 0 8px 4px 0;

  &:hover {
    .v-chip__close {
      max-height: 14px !important;
      max-width: 14px !important;
      font-size: 14px !important;
      margin-inline-start: 4px !important;
      margin-inline-end: -2px !important;
      display: flex;
    }
  }
}

:deep(.v-chip--variant-outlined) {
  border: 2px solid;
  @include themify() {
    color: themed('color-black');
  }
}

:deep(.v-chip.v-chip--size-default) {
  padding: 4px 6px;
}

:deep(.v-chip__content) {
  font-size: 0.625rem;
  font-weight: 700;
}
</style>
